import * as THREE from 'three'

/**
 * 导入模型渲染槽资格判定策略。
 *
 * 该类只返回结构化判定，不创建资源或修改场景；Coordinator 据此选择正式发布或保留
 * 普通 Mesh，使任一不兼容输入都能沿原有加载链稳定降级。
 */
export class ImportedModelInstancingEligibilityPolicy {
  /** 热路径外复用的缩放分解结果。 */
  private readonly scale = new THREE.Vector3()

  /** 热路径外复用的平移分解结果。 */
  private readonly position = new THREE.Vector3()

  /** 热路径外复用的旋转分解结果。 */
  private readonly quaternion = new THREE.Quaternion()

  /**
   * 判断一个渲染槽是否可以进入 InstancedMesh。
   *
   * @param slot 待判断渲染槽。
   * @param minimumOccurrenceCount 最少 occurrence 数量；装配体共享实例默认为 2，阵列独立实例组允许 1。
   * @returns 资格结果与稳定降级原因。
   */
  public evaluate(
    slot: PuppyCAD.ImportedModelInstancingRenderSlot,
    minimumOccurrenceCount = 2,
  ): PuppyCAD.ImportedModelInstancingEligibilityResult {
    if (slot.members.length < minimumOccurrenceCount) {
      return this.reject(
        'insufficient-occurrences',
        `occurrence 数量不足 ${minimumOccurrenceCount} 个`,
      )
    }
    if (!slot.geometry.getAttribute('position')) {
      return this.reject('missing-position', 'Geometry 缺少 position 属性')
    }

    const knownOccurrenceIds = new Set<string>()
    for (const member of slot.members) {
      if (knownOccurrenceIds.has(member.occurrenceId)) {
        return this.reject('duplicated-occurrence-slot', '同一 occurrence 在渲染槽中出现多次')
      }
      knownOccurrenceIds.add(member.occurrenceId)

      const sourceMesh = member.sourceMesh
      if (sourceMesh instanceof THREE.SkinnedMesh) {
        return this.reject('skinned-mesh', '蒙皮网格不进入首轮实例化')
      }
      if ((sourceMesh.morphTargetInfluences?.length ?? 0) > 0) {
        return this.reject('morph-target', '包含 morph target 的网格不进入首轮实例化')
      }
      sourceMesh.updateWorldMatrix(true, false)
      if (sourceMesh.matrixWorld.determinant() <= 0) {
        return this.reject('negative-scale', '负缩放或镜像矩阵不受 InstancedMesh 支持')
      }
      sourceMesh.matrixWorld.decompose(this.position, this.quaternion, this.scale)
      if (!this.isUniformScale(this.scale)) {
        return this.reject('non-uniform-scale', '首轮实例化暂不接管非均匀缩放网格')
      }
    }

    return Object.freeze({ eligible: true })
  }

  /**
   * 判断缩放是否在容差内保持一致。
   *
   * @param scale 待判断三轴缩放。
   * @returns 是否为均匀缩放。
   */
  private isUniformScale(scale: THREE.Vector3): boolean {
    const tolerance = 1e-7
    return Math.abs(scale.x - scale.y) <= tolerance
      && Math.abs(scale.x - scale.z) <= tolerance
      && Math.abs(scale.y - scale.z) <= tolerance
  }

  /**
   * 创建统一的不合格结果。
   *
   * @param reasonCode 稳定原因码。
   * @param message 中文诊断。
   * @returns 不合格结果。
   */
  private reject(
    reasonCode: string,
    message: string,
  ): PuppyCAD.ImportedModelInstancingEligibilityResult {
    return Object.freeze({ eligible: false, reasonCode, message })
  }
}
